import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Contact } from '../types';

const Trash: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchTrashContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (debouncedSearch) params.search = debouncedSearch;
      params.page = currentPage;
      params.per_page = perPage;
      params.trashed = 'only';

      const response = await api.get('/contacts', params);
      setContacts(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch trash contacts', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, perPage]);

  useEffect(() => {
    fetchTrashContacts();
  }, [fetchTrashContacts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const handleSelectContact = (id: number) => {
    setSelectedContactIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContactIds([]);
    } else {
      setSelectedContactIds(contacts.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleRestore = async (id?: number) => {
    const idsToRestore = id ? [id] : selectedContactIds;
    if (idsToRestore.length === 0) {
      alert('Please select at least one contact to restore');
      return;
    }
    if (!window.confirm(`Are you sure you want to restore ${idsToRestore.length} contact(s)?`)) {
      return;
    }
    try {
      await api.post('/contacts/restore', { contact_ids: idsToRestore });
      alert('Contact(s) restored successfully!');
      setSelectedContactIds([]);
      setSelectAll(false);
      fetchTrashContacts();
    } catch (error) {
      alert('Failed to restore contact(s)');
    }
  };

  const handlePermanentDelete = async (id?: number) => {
    const idsToDelete = id ? [id] : selectedContactIds;
    if (idsToDelete.length === 0) {
      alert('Please select at least one contact to delete permanently');
      return;
    }
    if (!window.confirm(`Are you sure you want to permanently delete ${idsToDelete.length} contact(s)? This cannot be undone.`)) {
      return;
    }
    try {
      await api.post('/contacts/permanent-delete', { contact_ids: idsToDelete });
      alert('Contact(s) permanently deleted!');
      setSelectedContactIds([]);
      setSelectAll(false);
      fetchTrashContacts();
    } catch (error) {
      alert('Failed to delete contact(s)');
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm('Are you sure you want to empty the trash? This will permanently delete all contacts in the trash.')) {
      return;
    }
    try {
      await api.post('/contacts/empty-trash', {});
      alert('Trash emptied successfully!');
      setSelectedContactIds([]);
      setSelectAll(false);
      fetchTrashContacts();
    } catch (error) {
      alert('Failed to empty trash');
    }
  };

  const SkeletonRow = () => (
    <tr>
      <td style={{ padding: '16px 24px' }}><div style={{ width: '16px', height: '16px', background: '#e5e7eb', borderRadius: '4px' }}></div></td>
      <td style={{ padding: '16px 24px' }}><div style={{ width: '200px', height: '16px', background: '#e5e7eb', borderRadius: '4px' }}></div></td>
      <td style={{ padding: '16px 24px' }}><div style={{ width: '150px', height: '16px', background: '#e5e7eb', borderRadius: '4px' }}></div></td>
      <td style={{ padding: '16px 24px' }}><div style={{ width: '150px', height: '16px', background: '#e5e7eb', borderRadius: '4px' }}></div></td>
      <td style={{ padding: '16px 24px' }}><div style={{ width: '100px', height: '16px', background: '#e5e7eb', borderRadius: '4px' }}></div></td>
    </tr>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Trash</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Deleted contacts can be restored or permanently deleted</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleEmptyTrash}
            style={{
              padding: '12px 20px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🗑️</span> Empty Trash
          </button>
        </div>
      </div>

      {selectedContactIds.length > 0 && (
        <div style={{ 
          background: '#fef3c7', 
          border: '1px solid #f59e0b', 
          borderRadius: '8px', 
          padding: '12px 16px', 
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#92400e', fontSize: '0.875rem' }}>{selectedContactIds.length} contact(s) selected</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleRestore()}
              style={{
                padding: '8px 16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Restore
            </button>
            <button
              onClick={() => handlePermanentDelete()}
              style={{
                padding: '8px 16px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Delete Permanently
            </button>
          </div>
        </div>
      )}

      <div style={{ position: 'relative', flex: 1, minWidth: '300px', marginBottom: '24px' }}>
        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
        <input
          type="text"
          placeholder="Search trash..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '10px 12px 10px 40px',
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '0.875rem',
            outline: 'none'
          }}
        />
      </div>

      <div style={{ 
        background: 'white', 
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '48px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🗑️</div>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No deleted contacts</p>
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <tr key={contact.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 24px' }}>
                      <input
                        type="checkbox"
                        checked={selectedContactIds.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#6b7280'
                        }}>
                          {(contact.first_name?.[0] || contact.email[0]).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 500, color: '#111827' }}>
                          {contact.first_name} {contact.last_name}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '0.875rem' }}>{contact.email}</td>
                    <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '0.875rem' }}>{contact.company || '-'}</td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleRestore(contact.id)}
                          style={{
                            padding: '8px 16px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(contact.id)}
                          style={{
                            padding: '8px 16px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '20px',
          padding: '16px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} contacts
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                background: currentPage === 1 ? '#f3f4f6' : '#3b82f6',
                color: currentPage === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.875rem', color: '#374151', minWidth: '80px', textAlign: 'center' }}>
              Page {currentPage} of {Math.ceil(total / perPage)}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.ceil(total / perPage) >= prev + 1 ? prev + 1 : prev)}
              disabled={currentPage >= Math.ceil(total / perPage)}
              style={{
                padding: '8px 16px',
                background: currentPage >= Math.ceil(total / perPage) ? '#f3f4f6' : '#3b82f6',
                color: currentPage >= Math.ceil(total / perPage) ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: currentPage >= Math.ceil(total / perPage) ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trash;
